import { createFileRoute, getRouteApi, Link } from "@tanstack/react-router";
import { FormattedMessage } from "react-intl";
import { TransferAllocationCard } from "../components/transferAllocationCard";
import { FundSwitchAllocationCard } from "../components/fundSwitchAllocationCard";
import {
  Title,
  Text,
  Paper,
  Flex,
  Button,
  SimpleGrid,
  Box,
  NumberFormatter,
} from "@mantine/core";
import { PolicyRecommendations } from "../components/policyRecommendations";

import { useAdviceInputStore } from "../hooks/ui";
import {
  getAdviceQueryOptions,
  getCalculationQueryOptions,
  getTransferPolicyQueryOptions,
} from "../hooks/api";
import { useEffect } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import {
  splitPolicies,
  toFundSwitchPolicies,
} from "../components/policyRecommendations/policies";
import { LoaderComponent } from "../components/loaderComponent";
import { FooterButtons } from "../components/footerButtons";
import { AdviceSummaryCta } from "../components/adviceSummaryCta";
import { getStoredCredentials } from "../hooks/ui/customerDataStore";
import PoaExpiredBanner from "../components/poaValidation";
import { REPLAY_BLOCK_CLASS } from "../utils/statsig";

export const Route = createFileRoute("/advice-summary")({
  component: AdviceSummaryPage,
  loader: async ({ context }) => {
    const credentials = context.credentials?.sessionToken
      ? context.credentials
      : getStoredCredentials();
    if (!credentials?.sessionToken) {
      throw new Error("Credentials are missing");
    }
    const response = await Promise.all([
      context.queryClient.fetchQuery(getCalculationQueryOptions(credentials)),
      context.queryClient.fetchQuery(getAdviceQueryOptions(credentials)),
      context.queryClient.fetchQuery(
        getTransferPolicyQueryOptions(credentials),
      ),
    ]);
    return {
      calculation: response[0],
      advice: response[1],
      transferPolicies: Array.isArray(response[2]) ? response[2] : [],
    };
  },
  pendingComponent: () => <LoaderComponent />,
  errorComponent: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const queryClient = useQueryClient();
    return (
      <Flex direction="column" justify="center" mih="80vh" px="lg">
        <Title ff={"var(--wallet-font-family-sambla-sans)"}>
          Hoppsan! Ditt råd har inte hunnit färdigställas..
        </Title>
        <Paper p={"lg"} radius={"lg"} my={"xl"}>
          <Button
            fullWidth
            mb={"lg"}
            onClick={() =>
              queryClient.refetchQueries({
                queryKey: ["get-advice-by-id"],
              })
            }
          >
            Ladda om sidan{" "}
            <Box ml={"sm"} component="span">
              <ArrowPathIcon height={"16px"} />
            </Box>
          </Button>
          <Button
            component={Link}
            to="/pension-forecast"
            fullWidth
            variant="default"
          >
            Tillbaka
          </Button>
        </Paper>
      </Flex>
    );
  },
});

function AdviceSummaryPage() {
  const { retirementAge, updateRetirementAge, updateRiskPreference } =
    useAdviceInputStore((state) => state);
  const routeApi = getRouteApi("/advice-summary");
  const { calculation, advice, transferPolicies } = routeApi.useLoaderData();

  useEffect(() => {
    updateRetirementAge(calculation.parameters.zAge);
    updateRiskPreference(calculation.parameters.riskPreference);
  }, [calculation]);

  const { transfers } = splitPolicies(calculation, [
    ...transferPolicies,
    ...(advice?.insuranceAdvices ?? []),
  ]);
  const fundSwitches = toFundSwitchPolicies(calculation);

  const transferIds = new Set(transfers.map((p) => p.insuranceIdentifier));
  const hasTransfers = transfers.length > 0;
  const hasFundSwitches = fundSwitches.some(
    (p) => !transferIds.has(p.insuranceIdentifier),
  );

  return (
    <>
      <PoaExpiredBanner />
      <Title ff={"var(--wallet-font-family-sambla-sans)"} mt={"xl"} order={1}>
        Här är en sammanfattning av ditt råd
      </Title>
      <Text mb={"lg"}>
        Här är en sammanställning av din rådgivning och hur din situation
        förväntas förändras.
      </Text>
      <Paper p={"lg"} radius={"lg"} mb={"xl"}>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Box>
            <Text c="dimmed">Pensionsålder</Text>
            <Title
              c={"var(--wallet-color-sambla-green-shade-700)"}
              order={2}
              size="h4"
            >
              {retirementAge} år
            </Title>
          </Box>
          <Box>
            <Text c="dimmed">Pension per månad</Text>
            <Title
              c={"var(--wallet-color-sambla-green-shade-700)"}
              order={2}
              size="h4"
            >
              <NumberFormatter
                className={REPLAY_BLOCK_CLASS}
                value={
                  calculation.adviceSummary.advicedPensionAmount.totalPension
                }
                decimalScale={0}
                suffix=" SEK"
                thousandSeparator={" "}
                prefix="ca "
              />
            </Title>
          </Box>
          <Box>
            <Text c="dimmed">Mer i pension per månad</Text>
            <Title
              c={
                calculation.adviceSummary.pensionDifference < 0
                  ? "var(--wallet-color-sambla-red-shade-700)"
                  : "var(--wallet-color-sambla-green-shade-700)"
              }
              order={2}
              size="h4"
            >
              <NumberFormatter
                className={REPLAY_BLOCK_CLASS}
                value={calculation.adviceSummary.pensionDifference}
                decimalScale={0}
                thousandSeparator={" "}
                suffix={" SEK"}
              />
            </Title>
          </Box>
          <Box>
            <Text c="dimmed">Sparade avgifter</Text>
            <Title
              c={
                calculation.adviceSummary.feeDifference < 0
                  ? "var(--wallet-color-sambla-green-shade-700)"
                  : "var(--wallet-color-sambla-red-shade-700)"
              }
              order={2}
              size="h4"
            >
              <NumberFormatter
                className={REPLAY_BLOCK_CLASS}
                value={Math.abs(calculation.adviceSummary.feeDifference)}
                decimalScale={0}
                thousandSeparator={" "}
                suffix={" SEK"}
                prefix={calculation.adviceSummary.feeDifference < 0 ? "+" : "-"}
              />
            </Title>
          </Box>
        </SimpleGrid>
      </Paper>
      <PolicyRecommendations
        fundSwitches={fundSwitches}
        transfers={transfers}
        showOthersTab={false}
        title={
          <FormattedMessage
            id="adviceSummary.list.headline"
            defaultMessage="Rådet för dina försäkringar"
          />
        }
        description={null}
        transfersHeading={
          <FormattedMessage
            id="insuranceList.toBetransferred.title"
            defaultMessage="Att flytta till IdunLiv"
          />
        }
        adviceAge={calculation.parameters.zAge}
        transfersFooter={<TransferAllocationCard data={calculation} />}
        fundSwitchesFooter={
          <FundSwitchAllocationCard
            allocation={calculation.fundSwitchAllocation}
          />
        }
      />

      <FooterButtons>
        <Button component={Link} to="/pension-forecast" variant="default">
          Tillbaka
        </Button>
        <AdviceSummaryCta
          hasTransfers={hasTransfers}
          hasFundSwitches={hasFundSwitches}
        />
      </FooterButtons>
    </>
  );
}
